import { useState, useMemo } from "react";
import { QA_DATA, QA_CATEGORIES } from "../data/qa";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import Icon from "./Icon";

export default function QATab({ lang = "he" }) {
  const [open,   setOpen]   = useState(null);
  const [search, setSearch] = useState("");
  const [cat,    setCat]    = useState("הכל");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return QA_DATA.filter(item => {
      const matchCat    = cat === "הכל" || item.category === cat;
      const matchSearch = !q || item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [search, cat]);

  // Group by category for display
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(item => {
      if (!map[item.category]) map[item.category] = { icon: item.categoryIcon, items: [] };
      map[item.category].items.push(item);
    });
    return map;
  }, [filtered]);

  const handleSearch = (val) => {
    setSearch(val);
    setOpen(null); // collapse all when searching
    if (val && cat !== "הכל") setCat("הכל"); // widen scope when typing
  };

  return (
    <div dir={lang === "he" ? "rtl" : "ltr"} style={{ display:"flex", flexDirection:"column", gap:12, paddingBottom:20 }}>

      {/* Header */}
      <div style={{ background:UI.surface.raised, borderRadius:T.radius.xl, boxShadow: UI.shadow.card, padding:"18px 22px", border:`1px solid ${UI.line.hairline}`, borderTop:`2px solid ${UI.line.strong}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
          <div style={{ width:46, height:46, borderRadius:6, background:UI.fill.subtle, border:`1px solid ${UI.line.strong}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke={UI.text.secondary} strokeWidth="1.5"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="17" r="0.6" fill={UI.text.secondary}/>
            </svg>
          </div>
          <div>
            <div style={TYPE.h1}>שאלות נפוצות</div>
            <div style={{ fontSize:13, color:UI.text.secondary, marginTop:3 }}>{QA_DATA.length} שאלות · {QA_CATEGORIES.length} קטגוריות</div>
          </div>
        </div>

        {/* Search */}
        <div style={{ display:"flex", alignItems:"center", gap:8, background:UI.fill.subtle, border:`1px solid ${UI.line.hairline}`, borderRadius:6, padding:"10px 12px" }}>
          <Icon name="search" size={16} color={UI.text.secondary}/>
          <input
            dir="rtl"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="חפש שאלה..."
            style={{ flex:1, background:"transparent", border:"none", outline:"none", fontSize:14, color:UI.text.primary, fontFamily:FONT.body }}
          />
          {search && (
            <button onClick={() => handleSearch("")} aria-label="נקה חיפוש"
              style={{ display:"inline-flex", background:"none", border:"none", color:UI.text.secondary, cursor:"pointer", padding:0, lineHeight:1 }}><Icon name="x" size={16} color={UI.text.secondary}/></button>
          )}
        </div>
      </div>

      {/* Category filter chips */}
      <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:2 }}>
        {["הכל", ...QA_CATEGORIES].map(c => (
          <button key={c} onClick={() => { setCat(c); setOpen(null); }}
            style={{ background: cat===c ? UI.fill.subtle : "transparent",
                     border: `1px solid ${cat===c ? UI.line.strong : UI.line.hairline}`,
                     borderRadius:6, padding:"6px 14px", fontSize:13, fontWeight:700,
                     color: cat===c ? UI.text.primary : UI.text.muted, cursor:"pointer",
                     whiteSpace:"nowrap", flexShrink:0, fontFamily:FONT.body }}>
            {c}
          </button>
        ))}
      </div>

      {/* Results count when filtering */}
      {(search || cat !== "הכל") && (
        <div style={{ fontSize:13, color:UI.text.muted, paddingRight:2 }}>
          {filtered.length === 0 ? "לא נמצאו תוצאות" : `${filtered.length} שאלות`}
          {search && <span style={{ color:UI.text.primary }}> תואמות "{search}"</span>}
        </div>
      )}

      {/* Empty state */}
      {filtered.length === 0 && (
        <div style={{ textAlign:"center", padding:"40px 22px", color:UI.text.muted, fontSize:15,
                      background:UI.surface.raised, border:`1px solid ${UI.line.hairline}`, borderRadius:T.radius.xl, boxShadow: UI.shadow.card }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}><Icon name="search" size={32} color={UI.text.muted}/></div>
          לא נמצאו שאלות עבור "{search}"
          <div style={{ marginTop:8, fontSize:13 }}>נסה מילות חיפוש שונות</div>
        </div>
      )}

      {/* Grouped accordion */}
      {Object.entries(grouped).map(([category, { icon, items }]) => (
        <div key={category} style={{ display:"flex", flexDirection:"column", gap:6 }}>

          {/* Category heading */}
          <div style={{ display:"flex", alignItems:"center", gap:8, paddingRight:2 }}>
            <Icon name={icon} size={16} color={UI.text.secondary}/>
            <span style={TYPE.headingSub}>{category}</span>
            <span style={{ fontSize:12, color:UI.text.muted, fontWeight:600 }}>· {items.length}</span>
          </div>

          {items.map(item => {
            const isOpen = open === item.id;
            return (
              <div key={item.id}
                style={{ background:UI.surface.raised, border:`1px solid ${isOpen ? UI.line.strong : UI.line.hairline}`,
                         borderRadius:6, overflow:"hidden", transition:"border-color .18s" }}>
                <button
                  onClick={() => setOpen(isOpen ? null : item.id)}
                  style={{ width:"100%", background: isOpen ? UI.fill.subtle : "transparent",
                           border:"none", padding:"14px 16px", display:"flex", alignItems:"center",
                           gap:12, cursor:"pointer", textAlign:"right" }}
                >
                  <span style={{ flex:1, fontSize:15, fontWeight:700, color: UI.text.primary,
                                 lineHeight:1.5, textAlign:"right" }}>
                    {item.question}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    style={{ flexShrink:0, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition:"transform .18s" }}>
                    <polyline points="6,9 12,15 18,9" stroke={isOpen ? UI.text.primary : UI.text.secondary}
                              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {isOpen && (
                  <div style={{ padding:"0 16px 16px", borderTop:`1px solid ${UI.line.hairline}` }}>
                    <div style={{ paddingTop:12, fontSize:14, color:UI.text.secondary, lineHeight:1.85 }}>
                      {item.answer}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Contact footer */}
      {!search && cat === "הכל" && (
        <div style={{ background:UI.fill.subtle, border:`1px solid ${UI.line.hairline}`,
                      borderRadius:6, padding:"14px 16px", textAlign:"center" }}>
          <div style={{ fontSize:14, color:UI.text.secondary, lineHeight:1.7 }}>שאלה שלא מופיעה כאן?</div>
          {/* The one thing on this screen that has to be found, not just read —
              so it carries a hue rather than a text weight. */}
          <div style={{ fontSize:14, color:UI.categorical[0], fontWeight:700, marginTop:2 }}>nagardavid88@gmail.com</div>
        </div>
      )}
    </div>
  );
}
